import React, { useState, useEffect, Suspense } from "react"
import { Paper, TableRow, TableCell } from "@material-ui/core"

import { stableSort, getComparator } from "App/Component/ui/datatable/utils"

import {
  DatatablesFooterServerSide,
  Title,
  TableCellDefault,
  Loading,
  TableAction,
  ModalDeleteConfirm,
} from "App/Component/ui"

import { STYLE_DEFAULT_MASTER } from "App/constants/theme"

import { useDatatablesServerSide, useDatatables } from "App/hooks"
import {
  deleteTagihanByID,
  getByFilterTagihan,
  getDataEditTagihan,
  headCells,
  RESET_STATE_FORM_FILTER,
} from "./helpers"
import {
  defaultDateDisplay,
  defaultToastError,
  defaultToastSuccess,
  formatNumber,
  isNull,
} from "App/utils/helpers"

import FormFilterDaftarTagihan from "./FormFilterDaftarTagihan"
import { BILLING_INPUT_TAGIHAN } from "App/constants"
import { RenderStatusPembelian } from "../../Penjualan/DaftarPenjualan/helpers"
import {
  changeActiveMenu,
  changeActiveTab,
  changePagePropsInputTagihan,
} from "../../Wrapper/context/actions"
import { FreezeTable } from "components/organism"
import { useDispatchWrapper } from "hooks"
import { LoadingBackdrop } from "components/atoms"

export default function DaftarTagihan() {
  const classes = STYLE_DEFAULT_MASTER()
  const { dispatch, handleClickMenu } = useDispatchWrapper()

  const [rows, setRows] = useState([])
  const [formFilter, setFormFilter] = useState(RESET_STATE_FORM_FILTER)
  const [totalData, setTotalData] = useState(0)
  const [loading, setLoading] = useState(false)
  const [showModalDeleteConfirm, setShowModalDeleteConfirm] = useState(false)
  const [data, setData] = useState({})
  const [tagihanID, setTagihanID] = useState(0)

  const handleClickUpdate = async (row) => {
    setLoading(true)

    /**
     * Ubah menjadi reducer
     */

    Promise.resolve()
      .then(() => {
        dispatch(changeActiveMenu(BILLING_INPUT_TAGIHAN))
      })
      .then(async () => {
        const propsPageInputTagihan = await getDataEditTagihan(row)
        if (propsPageInputTagihan.state) {
          dispatch(changePagePropsInputTagihan(propsPageInputTagihan.data))
          dispatch(changeActiveTab(BILLING_INPUT_TAGIHAN))

          handleClickMenu({
            code: BILLING_INPUT_TAGIHAN,
            name: "Input Tagihan",
          })
        } else {
          defaultToastError(propsPageInputTagihan.message)
        }
        setLoading(false)
      })
  }

  const setNewData = async (
    pages = formFilter.pages,
    limit = formFilter.limit,
  ) => {
    setLoading(true)

    const res = await getByFilterTagihan(formFilter, pages, limit)
    if (res.state) {
      setRows(res.data)
      setTotalData(res.meta.count)
      setData({
        total: res.total,
        pembayaran: res.pembayaran,
        sisa: res.sisa,
      })
      setLoading(false)
    } else {
      setRows([])
      setLoading(false)
      defaultToastError(res.message)
    }
  }

  const {
    pagination,
    onNext,
    onPrevious,
    handleClickPageNumber,
    handleChangeLimit,
    resetPagination,
  } = useDatatablesServerSide({ setNewData: setNewData })

  useEffect(() => {
    ;(async () => {
      await setNewData()
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const clickDelete = async (id) => {
    setTagihanID(id)
    setShowModalDeleteConfirm(!showModalDeleteConfirm)
  }

  async function deleteList() {
    const res = await deleteTagihanByID(tagihanID)
    if (res.state) {
      defaultToastSuccess(res.message)
      await setNewData(pagination.page, pagination.limit)
    } else {
      defaultToastError(res.message)
    }

    setShowModalDeleteConfirm(false)
  }

  return (
    <Suspense fallback={<LoadingBackdrop />}>
      {showModalDeleteConfirm && (
        <ModalDeleteConfirm
          confirmHandle={() => deleteList()}
          setShowModalDeleteConfirm={() => setShowModalDeleteConfirm(false)}
        />
      )}
      <div className={classes.root}>
        <Title title={"Daftar Tagihan"} />

        <Paper
          className={classes.paper}
          style={{
            padding: "1rem",
          }}
        >
          <FormFilterDaftarTagihan
            formFilter={formFilter}
            setFormFilter={setFormFilter}
            setNewData={setNewData}
            data={data}
            withExportButton
            resetPagination={resetPagination}
          />
          <Loading show={loading} />
          <div className="mt-1">
            <TableDaftarTagihan
              rows={rows}
              classes={classes}
              handleClickUpdate={handleClickUpdate}
              clickDelete={clickDelete}
            />
            <DatatablesFooterServerSide
              totalData={totalData}
              pagination={pagination}
              handleChangeLimit={handleChangeLimit}
              onPrevious={onPrevious}
              onNext={onNext}
              currentPage={pagination.page}
              handleClickPageNumber={handleClickPageNumber}
            />
          </div>
        </Paper>
      </div>
    </Suspense>
  )
}

const TableDaftarTagihan = ({ rows, handleClickUpdate, clickDelete }) => {
  const { order, orderBy, handleRequestSort, length } = useDatatables({
    rows: rows,
    orderByDefault: "id",
    orderByRow: ["id"],
  })

  return (
    <FreezeTable
      headCells={headCells}
      handleRequestSort={handleRequestSort}
      order={order}
      length={length}
      maxHeight="900px"
      orderBy={orderBy}
    >
      {stableSort(rows, getComparator(order, orderBy)).map((row, index) => {
        const selisih =
          row?.selisihLebihDitanggung - row?.selisihKurangDitanggung
        return (
          <TableRow hover key={index}>
            <TableCell align="center">{index + 1}</TableCell>
            <TableCell align="center">
              <TableAction
                clickUpdate={() => handleClickUpdate(row)}
                clickDelete={() => clickDelete(row.id)}
              />
            </TableCell>
            <TableCell align="left" width="5%">
              {row?.id}
            </TableCell>
            <TableCell align="left" width="5%">
              {row?.IDGL}
            </TableCell>
            <TableCell align="left">
              {isNull(row?.id_rekap) ? "-" : row?.id_rekap}
            </TableCell>
            <TableCell align="left">
              {defaultDateDisplay(row?.tanggal_tagihan)}
            </TableCell>
            <TableCell align="left">{row?.nama}</TableCell>
            <TableCell align="left">{row?.rm_id}</TableCell>
            <TableCell align="left">{row?.no_sep}</TableCell>
            <TableCell align="left">
              {defaultDateDisplay(row?.rm_tanggal_lahir, false)}
            </TableCell>
            <TableCell align="left">{row?.nama_asuransi}</TableCell>
            <TableCell align="left">{row?.no_asuransi}</TableCell>
            <TableCell align="left">{row?.company_name}</TableCell>
            <TableCell align="left">{row?.doctor_name}</TableCell>
            <TableCell align="left">
              <RenderStatusPembelian status={row.statusTagihan} />
            </TableCell>
            <TableCell align="left">
              {defaultDateDisplay(row?.tanggal_kunjungan)}
            </TableCell>{" "}
            <TableCell align="left">
              {defaultDateDisplay(row?.tanggal_pulang)}
            </TableCell>
            <TableCell align="right">{formatNumber(selisih)}</TableCell>
            <TableCell align="right">
              {formatNumber(row?.selisih_paket)}
            </TableCell>
            <TableCell align="right">
              {formatNumber(row?.totalTagihan)}
            </TableCell>
            <TableCell align="right">
              {formatNumber(row?.total_bayar)}
            </TableCell>
            <TableCell align="right">
              {formatNumber(
                row.totalTagihan -
                  row.total_bayar +
                  selisih +
                  row?.selisih_paket,
              )}
            </TableCell>
            <TableCell align="left">{row?.alamat ?? " - "}</TableCell>
            <TableCellDefault row={row} />
          </TableRow>
        )
      })}
      {rows.length === 0 && (
        <TableRow>
          <TableCell align="center" colSpan={headCells.length}>
            Tidak ada data
          </TableCell>
        </TableRow>
      )}
    </FreezeTable>
  )
}
